import { useMemo } from "react";
import { useGetCheckAccessFunction } from "../../core/Authorization";
import { RouteDescription, useRoutesContext } from "../../core/routes";
import { MenuItemDescription } from "./types";

export type ExtendedMenuItemDescription = Omit<MenuItemDescription, "items"> & {
  items?: ExtendedMenuItemDescription[]
  requiredPermissions?: string | string[]
  requiredOneOfPermissions?: string | string[]
}

export const mapRoutesToMenuItems = (layer: string, routes?: RouteDescription[]): ExtendedMenuItemDescription[] =>
  routes
    ?.filter(x => (x.menuItem?.layer ?? "main") === layer)
    ?.map(x => {
      const items = mapRoutesToMenuItems(layer, x.children);
      return ({
        id: x.menuItem ? (x.menuItem?.id ?? x.id ?? x.menuItem?.path ?? x.menuItem?.externalPath ?? x.path) : undefined,
        label: x.menuItem?.label,
        icon: x.menuItem?.icon,
        category: x.menuItem?.category,
        path: x.menuItem?.path ?? x.path,
        externalPath: x.menuItem?.externalPath,
        onClick: x.menuItem?.onClick,
        items: items,
        badgeProps: x.menuItem?.badgeProps,
        componentProps: x.menuItem?.componentProps,
        requiredPermissions: x.requiredPermissions,
        requiredOneOfPermissions: Array.from(new Set([
          ...(Array.isArray(x.requiredOneOfPermissions) ? x.requiredOneOfPermissions : [ x.requiredOneOfPermissions ]),
          ...(Object.values(x.pageModePermissions || {})
            .flatMap(y => [
              ...(Array.isArray(y.requiredPermissions) ? y.requiredPermissions : [ y.requiredPermissions ]),
              ...(Array.isArray(y.requiredOneOfPermissions) ? y.requiredOneOfPermissions : [ y.requiredOneOfPermissions ]),
            ])
          ),
          ...(items.flatMap(x => x.requiredPermissions)),
          ...(items.flatMap(x => x.requiredOneOfPermissions)),
        ].filter(x => x))),
      }) as ExtendedMenuItemDescription;
    })
    .flatMap(x => x.id ? [ x ] : x.items || []) || [];

const filterAvailableMenuItems = (
  menuItems: ExtendedMenuItemDescription[] | undefined,
  checkItemAccess: (requiredPermissions?: (string | string[]), requiredOneOfPermissions?: (string | string[])) => {
    pending: boolean;
    isAccessAllowed: boolean
  },
): ExtendedMenuItemDescription[] =>
  menuItems?.filter(x => checkItemAccess(x.requiredPermissions, x.requiredOneOfPermissions).isAccessAllowed)
    .map(x => ({
      ...x,
      items: filterAvailableMenuItems(x.items, checkItemAccess),
    })) || [];


export function useBuildMenu(layer = "main"): MenuItemDescription[] {
  const routes = useRoutesContext();
  const menu = useMemo(() => mapRoutesToMenuItems(layer, routes), [ layer, routes ]);
  const checkItemAccess = useGetCheckAccessFunction();
  return useMemo(() => filterAvailableMenuItems(menu, checkItemAccess), [ menu, checkItemAccess ]) as MenuItemDescription[];
}